'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useOptionalListingActionContext } from '@/src/components/features/listing-actions';
import { useCurrentUser } from '@/src/lib/hooks/query';
import { analytics } from '@/src/components/features/property/lib';
import { contactTitleKey } from './contactSubmitLabel';
import { toHotpadsClickstreamListing } from '@/src/analytics/hotpads/track';
import type { ContactSubmissionContext } from './types';

const ContactFormOverlay = dynamic(() => import('./ContactFormOverlay').then((mod) => mod.ContactFormOverlay));

const PostContactFlowModal = dynamic(() => import('./PostContactFlowModal').then((mod) => mod.PostContactFlowModal));

/**
 * Compatibility hook for existing CTA call sites. The actual Contact modal is
 * hosted by ListingActionProvider so nested parent modals stay mounted while
 * contact/post-contact flows appear above them.
 */
export const useContactFormAction = (
  listing?: PropertyDetailListing | ExtendedListing | null,
  triggerLocation = 'home_details_halo',
  triggerObject = 'home_details_component|summary',
) => {
  const t = useTranslations('contactForm');
  const { data: currentUser } = useCurrentUser();
  const identityResolved = currentUser !== undefined;
  const listingActionContext = useOptionalListingActionContext();
  const [isOpen, setIsOpen] = useState(false);
  const [postContactContext, setPostContactContext] = useState<ContactSubmissionContext | null>(null);
  const clickstreamContext = useMemo(() => ({ triggerLocation, triggerObject }), [triggerLocation, triggerObject]);

  const openContactForm = useCallback(() => {
    if (!identityResolved) return;

    const title = t(contactTitleKey(listing));

    if (listingActionContext) {
      void listingActionContext.openContact(listing, {
        ...clickstreamContext,
        title,
      });
      return;
    }

    analytics.emitTrackContactClickEvent({
      ...clickstreamContext,
      listing: toHotpadsClickstreamListing(listing),
      phase: 'open',
    });
    setIsOpen(true);
  }, [clickstreamContext, identityResolved, listing, listingActionContext, t]);

  const handleSubmitSuccess = useCallback((context: ContactSubmissionContext) => {
    setIsOpen(false);
    setPostContactContext(context);
  }, []);

  if (listingActionContext) {
    return {
      openContactForm,
      contactFormOverlay: null,
      identityResolved,
    };
  }

  const contactFormOverlay =
    isOpen || postContactContext ? (
      <>
        {isOpen && (
          <ContactFormOverlay
            open={isOpen}
            onOpenChange={setIsOpen}
            title={t(contactTitleKey(listing))}
            listing={listing}
            mode="contact"
            clickstreamContext={clickstreamContext}
            onSubmitSuccess={handleSubmitSuccess}
          />
        )}
        <PostContactFlowModal context={postContactContext} onDone={() => setPostContactContext(null)} />
      </>
    ) : null;

  return {
    openContactForm,
    contactFormOverlay,
    identityResolved,
  };
};
